import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import SiteArrivalConfirmationChatComposer from './SiteArrivalConfirmationChatComposer';
import SiteArrivalConfirmationChatThread from './SiteArrivalConfirmationChatThread';
import { useSiteArrivalConfirmationFlow } from './useSiteArrivalConfirmationFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface SiteArrivalConfirmationChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenPdfPreview?: () => void;
}

const SiteArrivalConfirmationChatFlow: React.FC<SiteArrivalConfirmationChatFlowProps> = ({
  documentTitle,
  currentEmail,
  invitationId,
  onBack,
  onOpenPdfPreview,
}) => {
  const { t, language } = useLanguage();
  const wheelLocale = language === 'ja' ? 'ja' : language === 'vi' ? 'vi' : 'en';
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useSiteArrivalConfirmationFlow(currentEmail, invitationId, t);

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const completeThankYou = useMemo(
    () => t('submitDocuments.siteArrivalConfirmation.completeThankYou'),
    [t]
  );

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.siteArrivalConfirmation.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const atComplete = flow.isComplete;
  const { showIntro: showCompleteMessage, showCta: showCompleteCta } = useStaggeredPreviewCta(atComplete);

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(
      () => setRevealPhase(2),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS
    );
    const t3 = window.setTimeout(
      () => setRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [documentTitle, flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.isComplete,
    flow.editingCommitIndex,
    showCompleteMessage,
  ]);

  const canShowThread = revealPhase >= 3 && !flow.isLoading;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && (
              <AssistantBotRow animateIn>{t('submitDocuments.siteArrivalConfirmation.intro1')}</AssistantBotRow>
            )}
            {revealPhase >= 2 && (
              <AssistantBotRow animateIn>{t('submitDocuments.siteArrivalConfirmation.intro2')}</AssistantBotRow>
            )}
            {flow.isLoading ? (
              revealPhase >= 2 && (
                <AssistantBotRow animateIn>{t('submitDocuments.siteArrivalConfirmation.loading')}</AssistantBotRow>
              )
            ) : canShowThread ? (
              <SiteArrivalConfirmationChatThread flow={flow} />
            ) : null}
            {atComplete && showCompleteMessage && (
              <AssistantBotRow animateIn>{completeThankYou}</AssistantBotRow>
            )}
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <PdfPreviewActionButton
                label={t('submitDocuments.siteArrivalConfirmation.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            )}
            {!flow.isComplete && !flow.isLoading && canShowThread ? (
              <SiteArrivalConfirmationChatComposer flow={flow} wheelLocale={wheelLocale} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default SiteArrivalConfirmationChatFlow;
